import React from 'react'
import { InferGetServerSidePropsType } from 'next'
import { Container } from '@scentregroup/ui'
import { useRouter } from 'next/router'
import { setCacheHeaders } from '@scentregroup/shared/helpers/set-cache-headers'
import { FeaturedLinksGrid } from '@scentregroup/shared/components/featured-links-grid'
import { storyClickTracker } from '@scentregroup/shared/pages/index.analytics'
import Layout from '@scentregroup/shared/hub-components/layout'
import { fetchNationalStaticData } from '@scentregroup/shared/hub-components/universal-header'
import { localeToCountry } from '@scentregroup/shared/helpers/local-to-country'
import { CoreContainer } from '@hub/core-container'
import { LinkPagination } from '@scentregroup/shared/components/pagination'
import { Text } from '@hub/text'
import { makeApolloClient } from '../../lib/apolloClient'
import { Link } from '@scentregroup/shared/link'
import { getTheEditStories } from '../../lib/get-the-edit-stories'
import { GetServerSideProps, GetServerSidePropsContext } from '../../types/next'
import style from './index.module.scss'
import { getTopStripProps } from '@scentregroup/shared/hub-components/top-strip'

const ITEMS_PER_PAGE = 12

function getPageNumber(page?: string): number {
  if (page) {
    try {
      return parseInt(page, 10)
    } catch {
      return 1
    }
  }
  return 1
}

export const getServerSideProps: GetServerSideProps = async ({
  locale,
  params = {},
  res,
}: GetServerSidePropsContext) => {
  setCacheHeaders(res)
  const country = localeToCountry(locale)
  const client = makeApolloClient()

  const pageParam = Array.isArray(params.page) ? params.page[0] : params.page
  const page = getPageNumber(pageParam)

  const skip = (page - 1) * ITEMS_PER_PAGE

  const [nationalHeaderData, storyPage, topStripProps] = await Promise.all([
    fetchNationalStaticData(client, { country }),
    getTheEditStories(client, country, skip, ITEMS_PER_PAGE),
    getTopStripProps(client, country),
  ])

  return {
    props: {
      country,
      topStripProps,
      nationalHeaderData,
      storyPage: {
        ...storyPage,
        page,
      },
    },
  }
}

type PageType = React.FC<
  React.PropsWithChildren<
    InferGetServerSidePropsType<typeof getServerSideProps>
  >
>

const TheEdit: PageType = props => {
  const { country, topStripProps, nationalHeaderData, storyPage } = props
  const { stories, total, page } = storyPage

  const totalPages = Math.ceil(total / ITEMS_PER_PAGE)

  const router = useRouter()

  const metaData = {
    title: `The Edit at Westfield ${country}`,
    description: `Westfield ${country} - home to your favourite editorials. Explore our range of articles, products, brands, events & offers`,
    canonical: router.asPath,
  }

  return (
    <Layout
      country={country}
      topStrip={topStripProps}
      centre={undefined}
      header={{
        variant: 'national',
        data: nationalHeaderData,
        logoText: null,
      }}
      metaData={{ ...metaData, generateCanonical: true }}
      pageTitle="The Edit"
    >
      {stories.length > 0 && (
        <CoreContainer>
          <Text
            isBodyText
            color="quartz"
            sx={{ marginBottom: ['spacing-md', 'spacing-lg'] }}
          >
            <>Discover the latest in fashion, beauty and homewares.</>
          </Text>
          <FeaturedLinksGrid
            title=""
            Link={Link}
            featuredLinks={stories}
            itemsPerRow={3}
            trackers={{ clicked: storyClickTracker }}
          />
          {totalPages > 1 && (
            <Container
              type="large"
              className={style.marketplacePaginationContainer}
            >
              <div />
              <LinkPagination
                // The page here is 0 indexed
                currentPage={page - 1}
                totalPages={totalPages}
                makePageLink={(page: number) =>
                  page === 0 ? `/the-edit` : `/the-edit/${page + 1}`
                }
              />
            </Container>
          )}
        </CoreContainer>
      )}
    </Layout>
  )
}

// IDK why this extra wrapping layer is needed, maybe it can
// be deleted?
const TheEditPage: PageType = props => <TheEdit {...props} />

export default TheEditPage
